import { Badge } from "@/components/ui/badge";
import formatNumber from "@/utils/formatNumber";
import Image from "next/legacy/image";
import Link from "next/link";
import React, { useState, useContext } from "react";
import { fitIn } from "@/utils/imageHandler";
import { DotsThreeVertical, Heart, Plus } from "@phosphor-icons/react";
import acquired_badge from "@/assets/images/acquired_badge.svg";
import WishlistAddNew from "../WishlistAddNew";
import { useRouter } from "next/router";
import unicorn from "@/assets/images/unicorn.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "../ui/button";
import {
  createWishlist,
  createWishlistCategory,
  deleteWishlist,
} from "@/services/api/Wishlist";
import { useAtom } from "jotai";
import { loginPopup } from "@/Atoms";
import AppContext from "@/AppContext";
import InActiveIcon from "@/assets/images/inactive.png";

const StartupCard = ({
  user,
  startup,
  getWishlistCategories,
  wishlistCategories,
  setWishlistCategories,
  getUserWishlist,
  userWishlistIds,
  userWishlist,
}) => {
  const router = useRouter();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [openWishlistAction, setOpenWishlistAction] = useState(false);
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);

  const { state } = useContext(AppContext);

  const { planSubscription } = state;

  const hasSubscription =
    planSubscription && planSubscription.has_active_subscription;

  // Function to handle My Wishlist click without propagating
  const handleMyWishlistClick = (event, cat_id) => {
    event.stopPropagation();
    setWishlist(cat_id);
    // Add navigation or any other logic for My Wishlist here
  };

  const setWishlist = async (cat_id) => {
    const res = await createWishlist({
      category_id: cat_id,
      content_id: startup.id,
      type: 1,
    });
    getUserWishlist();
  };

  const removeWishlist = async (id) => {
    const res = await deleteWishlist(id);
    getUserWishlist();
  };

  const onRemoveWishlist = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const found = userWishlist.find((v) => v.content_id == startup.id);
    if (found) {
      removeWishlist(found.id);
    }
  };

  const handleChange = (e) => {
    if (!user) {
      return setOpenLoginPopup(true);
    }
    getWishlistCategories();
    setOpenWishlistAction(e);
  };

  const addWishlistCategory = async (payload) => {
    try {
      const res = await createWishlistCategory(payload);
      setWishlist(res.data.id);
      setWishlistCategories(null);
      getWishlistCategories();
      setIsAddDialogOpen(false);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Link
        href={`/startups/${startup.slug}-${startup.id}`}
        className='cursor-pointer block relative py-3 lg:px-4 lg:pb-4 lg:mt-[36px] lg:rounded-lg lg:bg-white/5 group lg:hover:bg-white/10 transition'
      >
        {userWishlistIds && (
          <div className='absolute flex items-center text-white gap-x-1 right-1.5 top-2'>
            {userWishlistIds.includes(startup.id) ? (
              <button type='button' onClick={onRemoveWishlist}>
                <Heart weight='fill' color='red' className='text-2xl' />
              </button>
            ) : (
              <DropdownMenu
                open={openWishlistAction}
                onOpenChange={handleChange}
              >
                <DropdownMenuTrigger asChild>
                  <button type='button'>
                    <Heart weight='bold' className='text-2xl' />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-[200px] max-h-[300px] overflow-y-auto'>
                  <DropdownMenuLabel className='text-xs font-medium text-white/40'>
                    Add to Watchlist
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.stopPropagation(); // Prevent click event from bubbling up
                      setIsAddDialogOpen(true); // Open the add dialog
                      setOpenWishlistAction(false);
                    }}
                  >
                    <Plus weight='bold' className='mr-2' />
                    <span>Create New Watchlist</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => handleMyWishlistClick(e, null)}
                  >
                    My Watchlist
                  </DropdownMenuItem>
                  {hasSubscription &&
                    wishlistCategories?.map((cat) => (
                      <DropdownMenuItem
                        key={cat.id}
                        onClick={(e) => handleMyWishlistClick(e, cat.id)}
                      >
                        {cat.name}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
        <div className='flex justify-between h-full lg:flex-col'>
          <div className='flex items-center gap-3 lg:block'>
            <div className='relative w-12 lg:w-[72px] lg:mb-1 lg:-mt-[36px] lg:-ml-2 overflow-hidden lg:border-8 rounded-full aspect-square border-background'>
              <Image
                src={fitIn(startup.logo_url, 400)}
                alt={startup.name}
                layout={"fill"}
                objectFit={"cover"}
              />
            </div>
            <div className='flex-1'>
              <p className='text-base font-semibold leading-tight lg:text-lg'>
                {startup.name}
              </p>
              {startup.formerly_named && (
                <p className='text-[9px] lg:text-[10px] -mt-1 mb-1'>
                  <span className='text-white/40'>Formerly </span>
                  <span className='text-white'>{startup.formerly_named}</span>
                </p>
              )}

              <div className='flex items-center gap-1 lg:block'>
                <span
                  className={`lg:hidden text-transparent bg-clip-text bg-gradient-to-tr inline-block ${
                    Number(startup.total_round_amount)
                      ? "lg:text-2xl tracking-tight text-base from-yellow-1 to-red-1"
                      : "lg:text-sm text-xs from-white/50 to-white/80"
                  } font-bold`}
                >
                  {startup.no_funding_rounds
                    ? "N/A"
                    : Number(startup.total_round_amount)
                    ? `$${formatNumber(startup.total_round_amount)}`
                    : "Undisclosed"}
                </span>

                {startup.city && startup.country && (
                  <>
                    <span className='lg:hidden opacity-40'>&bull;</span>
                    <p className='text-xs opacity-50 lg:mb-2'>
                      {startup.city?.name}, {startup.country?.country_name}
                    </p>
                  </>
                )}
              </div>

              {!!startup.is_inactive && (
                <div className='lg:absolute top-1.5 left-[88px]'>
                  <span className='inline-flex items-center justify-center px-2 py-1 text-[8px] tracking-wide uppercase font-medium leading-none rounded-full bg-gradient-to-tr from-red-700 to-red-500'>
                    Inactive
                  </span>
                </div>
              )}

              {startup.sectors.slice(0, 4).map((sector, index) => (
                <Badge
                  variant='outline'
                  key={index}
                  className='hidden lg:inline-flex border-dashed text-[10px] border-white/10 px-2 mr-0.5 font-normal text-white/70'
                >
                  {sector.name}
                </Badge>
              ))}

              {startup.sectors.length > 4 && (
                <Badge
                  variant='outline'
                  key='more'
                  className='hidden lg:inline-flex border-dashed text-[10px] border-white/10 px-2 mr-0.5 font-normal text-white/70'
                >
                  +{startup.sectors.length - 4}
                </Badge>
              )}

              {/* {startup.sectors.map((sector, index) => (
                  <Badge
                    variant='outline'
                    key={index}
                    className='hidden lg:inline-flex text-[10px] px-2 mr-0.5 font-normal text-white/70'
                  >
                    {sector.name}
                  </Badge>
                ))} */}
            </div>
          </div>
          <div className='hidden lg:flex items-end justify-between mt-2 lg:mt-3.5'>
            <p className='flex flex-col'>
              <span className='text-[10px] lg:text-[11px] opacity-40 mb-0.5'>
                Funds Raised
              </span>
              <span
                className={`text-transparent bg-clip-text bg-gradient-to-tr inline-block mr-1.5 ${
                  Number(startup.total_round_amount)
                    ? "lg:text-2xl tracking-tight -mb-1 text-lg from-yellow-1 to-red-1"
                    : "lg:text-sm text-xs from-white/50 to-white/80"
                } font-bold`}
              >
                {startup.no_funding_rounds
                  ? "N/A"
                  : Number(startup.total_round_amount)
                  ? `$${formatNumber(startup.total_round_amount)}`
                  : "Undisclosed"}
              </span>
            </p>
            <div className='flex -mr-0.5'>
              {!!startup.is_unicorn && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex items-center justify-center border-2 rounded-full w-7 h-7 border-background bg-gradient-to-tr from-purple-500 to-teal-500'>
                        <div className='relative aspect-[333/294] h-3.5'>
                          <Image src={unicorn} layout='fill' alt='' />
                        </div>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className='max-w-md text-black bg-white border-none'>
                      <p className='text-xs'>
                        Unicorns are considered any privately owned startup that
                        has successfully achieved a valuation of US $1 billion
                        or more at any time in its history. Companies which
                        achieved this status post-IPO are not considered
                        unicorns.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {!!startup.has_ipo_data && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='relative inline-flex items-center justify-center -ml-1.5 border-2 rounded-full w-7 h-7 border-background bg-gradient-to-tr from-red-500 to-yellow-500'>
                        <div className='relative text-[10px] font-bold tracking-tight'>
                          IPO
                        </div>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className='max-w-md text-black bg-white border-none'>
                      <p className='text-xs'>
                        Company offers its shares to the public for the first
                        time, thereby becoming a publicly traded company.
                        Companies typically go public to raise capital to fund
                        growth, pay off debt, or allow early investors and
                        founders to realize some of their gains by selling their
                        shares in the public market
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {!!startup.has_acquisition_data && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='relative -ml-1 border-2 rounded-full aspect-square h-7 border-background'>
                        <Image src={acquired_badge} layout='fill' alt='' />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className='max-w-md text-black bg-white border-none'>
                      <p className='text-xs'>
                        This startup has been acquired by another company or
                        investor, marking a significant milestone in its
                        journey.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      </Link>
      <WishlistAddNew
        addWishlistCategory={addWishlistCategory}
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
        wishlistCategories={wishlistCategories}
        hasSubscription={hasSubscription}
      />
    </>
  );
};

export default StartupCard;
